import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { Modal, ModalBody, ModalFooter, Button } from "reactstrap";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import {
  publicRoutes,
  authProtectedRoutes,
  authProtectedRoutesFull,
} from "./routes/index";
import { useSelector, useDispatch } from "react-redux";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "suneditor/dist/css/suneditor.min.css";

import "./App.css";
import { AlertTriangle } from "./Common/CommonImages";

import { askScreenShare, takeScreenSnapshot } from "./store/Screen/actions";
import { setCandidateFullExit, setTotalTime } from "./store/Candidate/action";
import { startCamera, takeSnapshot } from "./store/Camera/action";
import { handleBackButton, handleBeforeUnload } from "./Common/EventHandlers";
import {
  setRemainingTime,
  setFullScreen, // Import setFullScreen action
} from "./store/Test/action";
import {
  enterFullScreen,
  exitFullScreen,
  isCurrentlyFullscreen,
} from "./utils/fullscreen";
import TestSubmitMessage from "./Components/TestSubmitMessage";

import { handleSubmit } from "./utils/Utils.SubmitTest";
import { sendHeartbeat, sendHeartbeatBeacon } from "./services/assessment.api";
import { isMobileDevice } from "./utils/deviceDetection.util";
import FeedbackPage from "./Pages/Feedback/FeedbackPage";
import { setIsSubmitted } from "./store/Test/action";

const HEARTBEAT_INTERVAL = 30000; // 30 seconds

function App() {
  const dispatch = useDispatch();
  const testId = useSelector((state) => state.testReducer?.testId);
  const inviteId = useSelector((state) => state.candidateReducer?.id);
  const startTime = useSelector((state) => state.candidateReducer?.startTime);
  const isSubmitted = useSelector((state) => state.testReducer?.isSubmitted);
  const screen = useSelector((state) => state.testReducer?.screen);
  const testTime = useSelector((state) => state.testReducer.time);
  const resumeTime = useSelector((state) => state.candidateReducer.resumeDate);
  const previousSpentTime = useSelector(
    (state) => state.testReducer.previousSpentTime,
  );

  const mode = useSelector((state) => state.candidateReducer?.mode);
  const settings = useSelector((state) => state.testReducer?.setting);
  const checkCameraErrorIntervalId = useRef();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const prevIsFullscreenRef = useRef(false);
  const hasTimeUpHandled = useRef(false);
  const [showModal, setShowModal] = useState(false);
  const [showAutoLogoutModal, setShowAutoLogoutModal] = useState(false);
  const [renderFeedbackMode, setRenderFeedbackMode] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [action, setAction] = useState(null);
  const candidateExitCount = useSelector(
    (state) => state.candidateReducer.count || 0,
  );
  const fullScreenExitCountRef = useRef(candidateExitCount);

  const tabSwitchCount = useSelector((state) => state.candidateReducer.tab);

  const company = useSelector((state) => state.testReducer.company);

  //Camera states
  const isCameraRequired = useSelector(
    (state) => state.cameraReducer?.isCameraRequired,
  );

  const webcamStream = useSelector(
    (state) => state.cameraReducer?.webcamStream,
  );
  const cameraError = useSelector((state) => state.cameraReducer?.cameraError);

  const isCameraOn = useSelector((state) => state.cameraReducer?.isCameraOn);

  //Screen sharing states
  const isScreenSharingRequired = useSelector(
    (state) => state.screenReducer?.isScreenSharingRequired,
  );
  const isScreenShared = useSelector(
    (state) => state.screenReducer?.isScreenShared,
  );
  const screenStream = useSelector(
    (state) => state.screenReducer?.screenStream,
  );

  const handleEnterFullScreen = useCallback(() => {
    enterFullScreen()
      .then(() => {
        setIsFullscreen(true);
        prevIsFullscreenRef.current = true;
      })
      .catch((err) => {
        console.error("Error attempting to enter fullscreen mode:", err);
      });
  }, []);

  const handleExitFullScreen = useCallback(() => {
    exitFullScreen()
      .then(() => {
        setIsFullscreen(false);
        prevIsFullscreenRef.current = false;
      })
      .catch((err) => {
        console.error("Error attempting to exit fullscreen mode:", err);
      });
  }, []);

  const showTimeEndedMessage = () => {
    // Create a beautiful modal overlay
    const modal = document.createElement("div");
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
      font-family: 'Roboto', sans-serif;
    `;

    const modalContent = document.createElement("div");
    modalContent.style.cssText = `
      background: white;
      border-radius: 12px;
      padding: 40px;
      max-width: 500px;
      width: 90%;
      text-align: center;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
      animation: slideIn 0.3s ease-out;
    `;

    // Add CSS animation
    const style = document.createElement("style");
    style.textContent = `
      @keyframes slideIn {
        from { transform: translateY(-50px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
    `;
    document.head.appendChild(style);

    modalContent.innerHTML = `
      <div style="margin-bottom: 20px;">
        <div style="
          width: 60px;
          height: 60px;
          background: #fff3cd;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
          font-size: 30px;
        ">⏰</div>
        <h2 style="
          color: #333;
          margin: 0 0 15px 0;
          font-size: 24px;
          font-weight: 600;
        ">Time's Up!</h2>
        <p style="
          color: #666;
          margin: 0 0 20px 0;
          line-height: 1.5;
          font-size: 16px;
        ">Your assessment time has ended. You cannot continue further. Please provide your feedback to complete your assessment.</p>
        <div style="
          background: #e7f5ff;
          border-left: 4px solid #1976d2;
          padding: 15px;
          border-radius: 4px;
          margin: 20px 0;
        ">
          <p style="
            color: #1976d2;
            margin: 0;
            font-size: 14px;
            font-weight: 500;
          ">Your assessment has been automatically submitted. Please provide your feedback to complete the process.</p>
        </div>
        <button onclick="this.parentElement.parentElement.parentElement.remove(); redirectToFeedback();" style="
          background: #ff6812;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 6px;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s;
        " onmouseover="this.style.background='#e55a0f'" onmouseout="this.style.background='#ff6812'">
          Complete Assessment
        </button>
      </div>
    `;

    modal.appendChild(modalContent);
    document.body.appendChild(modal);

    // Add global function for redirect
    window.redirectToFeedback = () => {
      const feedbackUrl = `${window.location.origin}/feedback`;

      // Remove all event listeners to prevent dialogs
      window.removeEventListener("beforeunload", () => {});
      window.removeEventListener("popstate", () => {});
      window.onbeforeunload = null;

      // Simply redirect to feedback page in the same tab
      window.location.href = feedbackUrl;
    };
  };

  useEffect(() => {
    const logOutOnInterfaceChnage = settings?.find(
      (setting) => setting.setting === "Logout on leaving a test interface",
    );
    if (logOutOnInterfaceChnage && logOutOnInterfaceChnage.active) {
      if (tabSwitchCount > 0 && !isSubmitted) {
        // Stop any multiple submissions
        dispatch(setIsSubmitted(true));
        
        // Show the termination modal instead of exiting fullscreen
        setShowAutoLogoutModal(true);

        // Submit silently in the background
        handleSubmit().catch((err) => {
          console.error("Auto submit failed", err);
        });
      }
    }
  }, [tabSwitchCount]);

  useEffect(() => {
    const hasExecuted = localStorage.getItem("hasExecuted");
    if (!hasExecuted) {
      localStorage.clear();
      sessionStorage.clear();
      localStorage.setItem("hasExecuted", true);
    }
  }, []);

  useEffect(() => {
    if (startTime !== null) {
      const timer = setInterval(() => {
        const totalTime =
          Math.floor((Date.now() - startTime) / 1000) +
          (previousSpentTime || 0);
        dispatch(setTotalTime(totalTime));
      }, 1000);

      return () => {
        clearInterval(timer);
      };
    }
  }, [startTime, previousSpentTime]);

  useEffect(() => {
    hasTimeUpHandled.current = false;
  }, [startTime]);

  useEffect(() => {
    const currentPath = window.location.pathname || "";
    if (currentPath.toLowerCase().startsWith("/feedback")) {
      return;
    }

    const baseTime = resumeTime !== null ? resumeTime : startTime;

    if (baseTime !== null) {
      let intervalId;
      let remainingTime = null;
      let messageShown = false;

      const convertToSeconds = (timeString) => {
        if (!timeString || typeof timeString !== "string") {
          console.error("Invalid time string:", timeString);
          return 0;
        }
        const [hours, minutes, seconds] = timeString.split(":").map(Number);
        if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) {
          console.error("Invalid time string:", timeString);
          return 0;
        }
        return hours * 3600 + minutes * 60 + seconds;
      };

      const totalSeconds = convertToSeconds(testTime);
      const endTime =
        baseTime +
        totalSeconds * 1000 -
        (resumeTime !== null ? previousSpentTime * 1000 : 0);

      const tenPercentTime = totalSeconds * 0.1;

      intervalId = setInterval(() => {
        if (isSubmitted) {
          clearInterval(intervalId);
          dispatch(setRemainingTime(0));
          return;
        }

        const remainingSeconds = Math.round((endTime - Date.now()) / 1000);

        if (remainingTime !== remainingSeconds) {
          remainingTime = remainingSeconds;
          dispatch(
            setRemainingTime(remainingSeconds > 0 ? remainingSeconds : 0),
          );
        }

        if (remainingSeconds <= tenPercentTime && !messageShown) {
          messageShown = true;
          document.body.style.animation = "shake 0.5s";
          document.body.style.animationIterationCount = "infinite";

          const messageElement = document.getElementById("message");
          messageElement.classList.remove("hidden");
          messageElement.classList.add("show");

          const minutes = Math.floor(remainingSeconds / 60);
          const seconds = remainingSeconds % 60;
          messageElement.innerText = `Only ${minutes} minutes and ${seconds} seconds left!`;

          setTimeout(() => {
            document.body.style.animation = "";
            messageElement.classList.remove("show");
            messageElement.classList.add("hidden");
          }, 2000);
        }

        if (remainingSeconds <= 5) {
          window.removeEventListener("beforeunload", handleBeforeUnload);
          window.removeEventListener("popstate", handleBackButton);
        }

        if (Date.now() >= endTime) {
          if (!hasTimeUpHandled.current) {
            hasTimeUpHandled.current = true;
            dispatch(setRemainingTime(0));
            dispatch(setTotalTime(totalSeconds));
            handleSubmit();

            // Show time ended message and redirect to feedback
            showTimeEndedMessage();
          }
          clearInterval(intervalId);
        }
      }, 1000);

      return () => {
        clearInterval(intervalId);
      };
    }
  }, [startTime, isSubmitted, testTime, resumeTime]);

  // Unified Integrity/Proctoring Requirement Monitor
  useEffect(() => {
    // Skip checks if we are on the feedback page or test isn't active
    const currentPath = window.location.pathname || "";
    if (
      !inviteId ||
      isSubmitted ||
      currentPath.toLowerCase().startsWith("/feedback")
    ) {
      // If we were showing an integrity modal but now we're done, close it
      if (
        action?.action === "enterFullScreen" ||
        action?.action === "shareScreen" ||
        action?.action === "startCamera"
      ) {
        setShowModal(false);
      }
      return;
    }

    // 1. Screen Sharing Check (Priority 1)
    // We check this first because initiating screen share often exits fullscreen
    const isScreenRequiredBySettings =
      settings?.some(
        (s) => s.setting === "Take screen snapshots on tab switch" && s.active,
      ) && !isMobileDevice();
    if (isScreenRequiredBySettings && !screenStream) {
      setAlertMessage(
        "Screen sharing has stopped or is required. Please share your screen to continue.",
      );
      setAction({ action: "shareScreen", actionText: "Share Screen" });
      setShowModal(true);
      return;
    }

    // 2. Camera Check (Priority 2)
    const isWebcamRequiredBySettings = settings?.some(
      (s) =>
        (s.setting === "Take Snapshots via webcam every 30 second" ||
          s.setting === "Take Snapshots via webcam every 30 seconds") &&
        s.active,
    );
    if (isWebcamRequiredBySettings && !webcamStream) {
      // If there's an explicit camera error, let that effect handle it for specific messaging
      if (!cameraError) {
        setAlertMessage(
          "Camera has stopped or is required. Please start your camera to continue.",
        );
        setAction({ action: "startCamera", actionText: "Start Camera" });
        setShowModal(true);
        return;
      }
    }

    // 3. Fullscreen Check (Priority 3 - Only enforce after media is set up)
    if (screen && !isFullscreen) {
      if (prevIsFullscreenRef.current === true) {
        fullScreenExitCountRef.current += 1;
        dispatch(setCandidateFullExit(fullScreenExitCountRef.current));
      }
      setAlertMessage("Do not exit the full screen.");
      setAction({ action: "enterFullScreen", actionText: "Enter Full Screen" });
      setShowModal(true);
      prevIsFullscreenRef.current = isFullscreen;
      return;
    }
    prevIsFullscreenRef.current = isFullscreen;

    // If we reached here, it means all active integrity checks passed.
    // Automatically close the modal if it was opened by one of these systemic checks
    // and there is no active camera error being displayed.
    if (
      !cameraError &&
      (action?.action === "enterFullScreen" ||
        action?.action === "shareScreen" ||
        action?.action === "startCamera")
    ) {
      setShowModal(false);
    }
  }, [
    inviteId,
    isSubmitted,
    screen,
    isFullscreen,
    settings,
    screenStream,
    webcamStream,
    cameraError,
    dispatch,
  ]);

  // Brute force cleanup for stuck Bootstrap backdrops
  useEffect(() => {
    if (!showModal) {
      document.body.classList.remove("modal-open");
      const backdrops = document.getElementsByClassName("modal-backdrop");
      while (backdrops.length > 0) {
        backdrops[0].parentNode.removeChild(backdrops[0]);
      }
    }
  }, [showModal]);

  useEffect(() => {
    // If fullscreen is not required anymore (e.g. test submitted) but we are still in fullscreen
    // But DON'T auto-exit if we are on the feedback page (user wants it to stay FS there)
    const currentPath = window.location.pathname || "";
    if (
      (!screen || isSubmitted) &&
      isFullscreen &&
      !currentPath.toLowerCase().startsWith("/feedback")
    ) {
      handleExitFullScreen();
    }
  }, [screen, isSubmitted, isFullscreen, handleExitFullScreen]);

  useEffect(() => {
    if (mode) {
      const handleFullScreenChange = () => {
        const isFS = isCurrentlyFullscreen();
        setIsFullscreen(isFS);
      };

      const handleResize = () => {
        // Fallback for F11 or other ways to enter fullscreen that don't trigger the API
        const isFS =
          isCurrentlyFullscreen() ||
          window.innerHeight === window.screen.height;
        setIsFullscreen(isFS);
      };

      document.addEventListener("fullscreenchange", handleFullScreenChange);
      document.addEventListener("mozfullscreenchange", handleFullScreenChange);
      document.addEventListener(
        "webkitfullscreenchange",
        handleFullScreenChange,
      );
      document.addEventListener("msfullscreenchange", handleFullScreenChange);
      window.addEventListener("resize", handleResize);

      setIsFullscreen(
        isCurrentlyFullscreen() || window.innerHeight === window.screen.height,
      );

      return () => {
        document.removeEventListener(
          "fullscreenchange",
          handleFullScreenChange,
        );
        document.removeEventListener(
          "mozfullscreenchange",
          handleFullScreenChange,
        );
        document.removeEventListener(
          "webkitfullscreenchange",
          handleFullScreenChange,
        );
        document.removeEventListener(
          "msfullscreenchange",
          handleFullScreenChange,
        );
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [mode]);

  useEffect(() => {
    let intervalId;

    const isWebcamSnapshotActive = settings?.some(
      (setting) =>
        (setting.setting === "Take Snapshots via webcam every 30 second" ||
          setting.setting === "Take Snapshots via webcam every 30 seconds") &&
        setting.active,
    );

    if (
      isCameraRequired &&
      isCameraOn &&
      webcamStream &&
      inviteId &&
      isWebcamSnapshotActive
    ) {
      console.log("🚀 Starting 30-second webcam snapshot interval");
      intervalId = setInterval(async () => {
        console.log("📸 Taking 30-second webcam snapshot...");
        try {
          await dispatch(takeSnapshot(webcamStream, testId, inviteId));
        } catch (err) {
          if (!axios.isCancel(err) && !err.cancelled) {
            console.error("Webcam snapshot failed:", err);
          }
        }
      }, 30000);
    }

    return () => {
      if (intervalId) {
        console.log("🛑 Clearing 30-second webcam snapshot interval");
        clearInterval(intervalId);
      }
    };
  }, [
    isCameraRequired,
    isCameraOn,
    inviteId,
    webcamStream,
    settings,
    testId,
    dispatch,
  ]);

  // Auto-restart camera if settings require it and it is currently off (e.g. after refresh)
  useEffect(() => {
    const isWebcamSnapshotActive = settings?.some(
      (setting) =>
        (setting.setting === "Take Snapshots via webcam every 30 second" ||
          setting.setting === "Take Snapshots via webcam every 30 seconds") &&
        setting.active,
    );

    if (isWebcamSnapshotActive && inviteId && !isCameraOn) {
      dispatch(startCamera());
    }
  }, [settings, inviteId, isCameraOn, dispatch]);

  const checkCameraError = () => {
    if (cameraError !== null) {
      if (cameraError.name === "NotAllowedError") {
        setAlertMessage(
          <span>
            Camera access is blocked. Please follow these steps:
            <br />
            <br />
            1. Click the <strong>lock icon</strong> or{" "}
            <strong>camera icon</strong> in your browser's address bar
            <br />
            2. Select "Allow" for camera permissions
            <br />
            3. Click "Start Camera" below to retry
            <br />
            <br />
            For detailed instructions,{" "}
            <a
              href="https://support.google.com/chrome/answer/2693767?hl=en&co=GENIE.Platform%3DDesktop"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1976d2", textDecoration: "underline" }}
            >
              click here
            </a>
            .
          </span>,
        );
        setAction({
          action: "startCamera",
          actionText: "Start Camera",
        });
        setShowModal(true);
      }
      if (cameraError.name === "NotFoundError") {
        setAlertMessage(
          "No camera found. Please connect a camera to continue the test.",
        );
        setAction({
          action: "startCamera",
          actionText: "Start Camera",
        });
        setShowModal(true);
      }
      if (cameraError.name === "NotReadableError") {
        setAlertMessage(
          "The camera is being used by another application. Please close the application to continue the test.",
        );
        setAction({
          action: "startCamera",
          actionText: "Start Camera",
        });
        setShowModal(true);
      }
      if (cameraError.name === "OverconstrainedError") {
        setAlertMessage(
          "The camera is not supported. Please connect a supported camera to continue the test.",
        );
        setAction({
          action: "startCamera",
          actionText: "Start Camera",
        });
        setShowModal(true);
      }
      if (cameraError.name === "SecurityError") {
        setAlertMessage(
          "The camera is disabled by the browser. Please enable the camera to continue the test.",
        );
        setAction({
          action: "startCamera",
          actionText: "Start Camera",
        });
        setShowModal(true);
      }

      if (cameraError.name === "AbortError" && isCameraRequired) {
        setAlertMessage(
          "The camera is disconnected. Please connect the camera to continue the test.",
        );
        setAction({
          action: "startCamera",
          actionText: "Start Camera",
        });
        setShowModal(true);
      }
    }

    if (cameraError === null) {
      // Only close modal if it was opened by camera error
      if (action?.action === "startCamera") {
        setShowModal(false);
      }
      stopCheckCameraErrorInterval();
    }
  };

  const startCheckCameraErrorInterval = () => {
    if (!checkCameraErrorIntervalId.current) {
      checkCameraErrorIntervalId.current = setInterval(() => {
        checkCameraError();
      }, 1000);
    }
  };
  const stopCheckCameraErrorInterval = () => {
    if (checkCameraErrorIntervalId.current) {
      clearInterval(checkCameraErrorIntervalId.current);
      checkCameraErrorIntervalId.current = null;
    }
  };

  useEffect(() => {
    if (cameraError !== null && isCameraRequired && inviteId) {
      startCheckCameraErrorInterval();
    } else {
      checkCameraError();
    }
  }, [cameraError, isCameraRequired, inviteId]);

  // Screen share check - consolidated into the unified integrity monitor above
  // useEffect(() => ... )

  useEffect(() => {
    const isEnterDetailsPage = () => {
      return window.location.pathname.includes("/enter-details");
    };

    settings?.forEach((setting) => {
      if (
        setting.setting === "Turn off copy paste from external sources" &&
        setting.active
      ) {
        document.addEventListener("mousedown", (e) => {
          if (
            e.target.nodeName !== "INPUT" &&
            e.target.nodeName !== "TEXTAREA"
          ) {
            e.preventDefault();
            e.stopPropagation();
          }
        });
        document.addEventListener("copy", (e) => {
          if (!isEnterDetailsPage()) {
            e.preventDefault();
            e.stopPropagation();
          }
        });
        document.addEventListener("cut", (e) => {
          if (!isEnterDetailsPage()) {
            e.preventDefault();
            e.stopPropagation();
          }
        });
        document.addEventListener("paste", (e) => {
          if (!isEnterDetailsPage()) {
            e.preventDefault();
            e.stopPropagation();
          }
        });
      }
    });
  }, [settings]);

  useEffect(() => {
    const disablePrintScreen = (e) => {
      if (e.key === "PrintScreen") {
        navigator.clipboard.writeText(""); // Clears clipboard
        alert("Screenshots are disabled during this assessment.");
      }
    };

    document.addEventListener("keyup", disablePrintScreen);

    return () => {
      document.removeEventListener("keyup", disablePrintScreen);
    };
  }, []);

  // Session Heartbeat Logic
  useEffect(() => {
    let heartbeatIntervalId;

    if (inviteId && !isSubmitted) {
      // Send initial heartbeat
      sendHeartbeat(inviteId).catch((err) =>
        console.warn("Heartbeat failed:", err),
      );

      // Setup regular interval
      heartbeatIntervalId = setInterval(() => {
        sendHeartbeat(inviteId).catch((err) =>
          console.warn("Interval heartbeat failed:", err),
        );
      }, HEARTBEAT_INTERVAL);

      // Send beacon heartbeat on unload to provide immediate closure signal
      const handleUnloadHeartbeat = () => {
        sendHeartbeatBeacon(inviteId);
      };

      window.addEventListener("beforeunload", handleUnloadHeartbeat);

      return () => {
        clearInterval(heartbeatIntervalId);
        window.removeEventListener("beforeunload", handleUnloadHeartbeat);
      };
    }
  }, [inviteId, isSubmitted]);

  // useEffect(() => {
  //   const detectDevTools = () => {
  //     const threshold = 160;
  //     const widthDiff = window.outerWidth - window.innerWidth;
  //     const heightDiff = window.outerHeight - window.innerHeight;

  //     if (widthDiff > threshold || heightDiff > threshold) {
  //       alert("Developer tools detected! Please close them.");
  //       window.location.reload();
  //     }
  //   };

  //   window.addEventListener("resize", detectDevTools);

  //   return () => {
  //     window.removeEventListener("resize", detectDevTools);
  //   };
  // }, []);

  const handleAction = async () => {
    const currentAction = action?.action;

    // Immediately close modal to prevent backdrop from sticking during the heavy
    // browser transition to/from fullscreen.
    setShowModal(false);

    if (currentAction === "enterFullScreen") {
      handleEnterFullScreen();
    } else if (currentAction === "shareScreen") {
      dispatch(askScreenShare());
    } else if (currentAction === "startCamera") {
      dispatch(startCamera());
    }
  };

  const handleAutoLogoutToFeedback = () => {
    setShowAutoLogoutModal(false);
    setRenderFeedbackMode(true);
  };

  const toggleModal = () => setShowModal(!showModal);

  if (renderFeedbackMode) {
    return (
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <FeedbackPage />
        <ToastContainer />
      </Router>
    );
  }

  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <Routes>
        {publicRoutes.map((route, idx) => (
          <Route key={idx} path={route.path} element={<route.component />} />
        ))}
        {authProtectedRoutes.map((route, idx) => (
          <Route
            key={idx}
            path={route.path}
            element={testId ? <route.component /> : <Navigate to="/" />}
          />
        ))}
        {authProtectedRoutesFull.map((route, idx) => (
          <Route
            key={idx}
            path={route.path}
            element={testId ? <route.component /> : <Navigate to="/" />}
          />
        ))}
        {!testId && <Route path="/*" element={<Navigate to="/" />} />}
      </Routes>
      <Modal
        isOpen={showModal}
        toggle={toggleModal}
        className="alert-model"
        fade={false}
        centered
        backdrop="static"
        keyboard={false}
      >
        <div className="modal-header border-0 pb-0">
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={handleAction}
          ></button>
        </div>
        <ModalBody className="pt-0">
          <div className="alert-trangle-text">
            <img src={AlertTriangle} alt="" />
            {alertMessage}
          </div>
        </ModalBody>
        <ModalFooter className="review-footer border-0">
          <Button className="btn-warning-modal" onClick={handleAction}>
            {action?.actionText || "Continue"}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={showAutoLogoutModal}
        backdrop="static"
        keyboard={false}
        centered
        className="termination-modal"
        style={{
          maxWidth: "450px",
          fontFamily: "'Inter', 'Roboto', sans-serif"
        }}
      >
        <ModalBody className="p-0 text-center" style={{ borderRadius: "12px", overflow: "hidden" }}>
          {/* Header section with gradient and icon */}
          <div style={{
            background: "linear-gradient(135deg, #ff4e50 0%, #f9d423 100%)",
            padding: "40px 20px",
            color: "white"
          }}>
            <div style={{
              width: "80px",
              height: "80px",
              backgroundColor: "rgba(255,255,255,0.2)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 15px auto",
              boxShadow: "0 4px 15px rgba(0,0,0,0.1)"
            }}>
              <img src={AlertTriangle} alt="Warning" style={{ width: 44, height: 44, filter: "brightness(0) invert(1)" }} />
            </div>
            <h3 style={{ margin: 0, fontWeight: 700, letterSpacing: "-0.5px" }}>Assessment Terminated</h3>
          </div>

          {/* Body section */}
          <div style={{ padding: "30px 25px", backgroundColor: "#fff" }}>
            <p style={{ color: "#555", fontSize: "15px", lineHeight: "1.5", marginBottom: "20px" }}>
              Your assessment was automatically ended due to a violation of the strict proctoring rules.
            </p>
            
            <div style={{
              backgroundColor: "#fff5f5",
              border: "1px solid #ffdcd5",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "25px",
              textAlign: "left"
            }}>
              <p style={{ margin: 0, fontSize: "13px", color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>Detected Violation</p>
              <p style={{ margin: "5px 0 0 0", color: "#d93025", fontSize: "16px", fontWeight: 600, display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ display: "inline-block", width: "8px", height: "8px", backgroundColor: "#d93025", borderRadius: "50%" }}></span>
                Tab Switching / Leaving Interface
              </p>
            </div>

            <p style={{ color: "#777", fontSize: "14px", marginBottom: "25px" }}>
              You are no longer allowed to continue. Please complete the mandatory feedback form before exiting.
            </p>

            <Button 
              onClick={handleAutoLogoutToFeedback} 
              style={{ 
                width: "100%", 
                backgroundColor: "#2e3135", 
                color: "#fff",
                border: "none",
                padding: "12px",
                borderRadius: "6px",
                fontWeight: 600,
                fontSize: "15px",
                boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                transition: "transform 0.2s"
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = "scale(1.02)"}
              onMouseOut={(e) => e.currentTarget.style.transform = "scale(1)"}
            >
              Continue to Feedback
            </Button>
          </div>
        </ModalBody>
      </Modal>

      {isSubmitted && !showAutoLogoutModal && !renderFeedbackMode && <TestSubmitMessage company={company} />}
      <div id="message" className="hidden">
        Only 10 minutes left!
      </div>
      <ToastContainer />
    </Router>
  );
}

export default App;
